import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Eye, EyeOff, GripHorizontal, Library, Maximize2, Minimize2, Pencil, Play, PlayCircle, Settings2, Trash2, X } from "lucide-react";
import { useLocation } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HIDE_KEY = "wz_tut_hidden";

const pageKey = (path) => {
  if (path.startsWith("/finance")) return `fin:${path.replace(/^\/finance\/?/, "").split("/")[0] || "dashboard"}`;
  if (path.startsWith("/employee")) return "portal";
  if (window.__wzView) return `dash:${window.__wzView}`;
  return null;
};
const hidden = () => { try { return JSON.parse(localStorage.getItem(HIDE_KEY)) || []; } catch { return []; } };

const OwnerEditor = ({ k, cur, onSaved, onClose }) => {
  const [url, setUrl] = useState(cur ? `https://youtu.be/${cur.video_id}` : "");
  const [title, setTitle] = useState(cur?.title || "");
  const save = async () => {
    try { await axios.put(`${API}/tutorials/${encodeURIComponent(k)}`, { url, title }); toast.success("تم حفظ فيديو هذه الصفحة"); onSaved(); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحفظ"); }
  };
  const del = async () => {
    try { await axios.delete(`${API}/tutorials/${encodeURIComponent(k)}`); toast.success("تم حذف الفيديو"); onSaved(); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحذف"); }
  };
  return (
    <div dir="rtl" className="fixed bottom-24 left-4 z-[70] w-80 space-y-2 rounded-2xl border border-slate-200 bg-white p-3 shadow-2xl" data-testid="tut-owner-editor">
      <div className="flex items-center justify-between"><p className="text-sm font-bold text-slate-800">فيديو تعليمي لهذه الصفحة</p>
        <button onClick={onClose} className="rounded p-1 text-slate-400 hover:bg-slate-100"><X className="h-4 w-4" /></button></div>
      <p className="font-mono text-[10px] text-slate-400" dir="ltr">{k}</p>
      <input value={url} onChange={(e) => setUrl(e.target.value)} dir="ltr" placeholder="https://youtu.be/…" className="h-9 w-full rounded-lg border border-slate-200 px-2 text-sm" data-testid="tut-url" />
      <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="عنوان (اختياري): كيف تستخدم صفحة الرواتب" className="h-9 w-full rounded-lg border border-slate-200 px-2 text-sm" data-testid="tut-title" />
      <div className="flex gap-2">
        <button onClick={save} className="h-9 flex-1 rounded-lg bg-rose-600 text-sm font-bold text-white hover:bg-rose-700" data-testid="tut-save">حفظ</button>
        {cur && <button onClick={del} className="grid h-9 w-9 place-items-center rounded-lg border border-rose-200 text-rose-600 hover:bg-rose-50" data-testid="tut-delete"><Trash2 className="h-4 w-4" /></button>}
      </div>
    </div>
  );
};

const Menu = ({ items, onClose, tid }) => (
  <div className="absolute bottom-full left-0 mb-2 w-60 overflow-hidden rounded-xl border border-slate-200 bg-white py-1 shadow-xl" data-testid={tid}>
    {items.map(([Icon, label, fn, id]) => (
      <button key={id} onClick={() => { onClose(); fn(); }} data-testid={id} className="flex w-full items-center gap-2 px-3 py-2 text-start text-sm text-slate-700 hover:bg-slate-50"><Icon className="h-4 w-4 text-slate-500" /> {label}</button>))}
  </div>
);

export const Player = ({ v, onClose }) => {
  const [big, setBig] = useState(false);
  const [pos, setPos] = useState(null);
  const drag = useRef(null);
  const w = big ? Math.min(640, window.innerWidth - 16) : Math.min(340, window.innerWidth - 16);
  const down = (e) => { const r = e.currentTarget.parentElement.getBoundingClientRect(); drag.current = { sx: e.clientX, sy: e.clientY, x: r.left, y: r.top }; e.currentTarget.setPointerCapture?.(e.pointerId); };
  const move = (e) => { const d = drag.current; if (!d) return; setPos({ x: Math.max(4, Math.min(window.innerWidth - w - 4, d.x + e.clientX - d.sx)), y: Math.max(4, Math.min(window.innerHeight - 80, d.y + e.clientY - d.sy)) }); };
  const style = pos ? { left: pos.x, top: pos.y, width: w } : { left: 16, bottom: 96, width: w };
  return (
    <div className="fixed z-[70] overflow-hidden rounded-2xl border border-slate-700 bg-slate-900 shadow-2xl" style={style} data-testid="tut-player">
      <div className="flex cursor-move touch-none select-none items-center gap-2 px-2 py-1.5 text-white" dir="rtl" onPointerDown={down} onPointerMove={move} onPointerUp={() => { drag.current = null; }}>
        <GripHorizontal className="h-4 w-4 shrink-0 text-slate-400" />
        <p className="min-w-0 flex-1 truncate text-xs font-semibold">{v.title || "فيديو تعليمي"}</p>
        <button onPointerDown={(e) => e.stopPropagation()} onClick={() => setBig(!big)} className="rounded p-1 hover:bg-white/10" data-testid="tut-resize">{big ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}</button>
        <button onPointerDown={(e) => e.stopPropagation()} onClick={onClose} className="rounded p-1 hover:bg-white/10" data-testid="tut-close"><X className="h-4 w-4" /></button>
      </div>
      <div className="relative w-full" style={{ paddingTop: "56.25%" }}>
        <iframe title="tutorial" className="absolute inset-0 h-full w-full" src={`https://www.youtube-nocookie.com/embed/${v.video_id}?autoplay=1&rel=0&modestbranding=1`}
          allow="autoplay; encrypted-media; picture-in-picture; fullscreen" allowFullScreen />
      </div>
    </div>
  );
};

// Floating «فيديو تعليمي» button + mini player on every app page
export const TutorialVideo = () => {
  const { pathname } = useLocation();
  const [data, setData] = useState({ items: {}, can_edit: false });
  const [k, setK] = useState(null);
  const [play, setPlay] = useState(false);
  const [edit, setEdit] = useState(false);
  const [hide, setHide] = useState(hidden);
  const [menu, setMenu] = useState("");
  const [preview, setPreview] = useState(null);
  const load = useCallback(() => axios.get(`${API}/tutorials`).then((r) => setData(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const upd = () => setK(pageKey(window.location.pathname));
    upd(); window.addEventListener("wz-view", upd);
    return () => window.removeEventListener("wz-view", upd);
  }, [pathname]);
  useEffect(() => { setPlay(false); setEdit(false); setMenu(""); }, [k]);
  if (!k) return null;
  const v = data.items[k];
  const off = hide.includes(k);
  const saveHide = (n) => { localStorage.setItem(HIDE_KEY, JSON.stringify(n)); setHide(n); };
  const hideIt = () => { const n = [...hide, k]; localStorage.setItem(HIDE_KEY, JSON.stringify(n)); setHide(n); setPlay(false); toast("تم إخفاء زر الفيديو لهذه الصفحة"); };
  return (
    <>
      <div className="fixed bottom-24 left-4 z-40 flex items-center gap-1" dir="rtl" data-testid="tut-widget">
        {v && !off && !play && (
          <span className="group inline-flex items-center overflow-hidden rounded-full bg-rose-600 text-white shadow-lg">
            <button onClick={() => setPlay(true)} className="inline-flex items-center gap-1.5 py-2 pe-2 ps-3 text-xs font-bold hover:bg-rose-700" data-testid="tut-open"><PlayCircle className="h-4 w-4" /> فيديو تعليمي</button>
            <button onClick={hideIt} title="إخفاء الزر لهذه الصفحة" className="border-s border-white/25 px-2 py-2 hover:bg-rose-700" data-testid="tut-hide"><EyeOff className="h-3.5 w-3.5" /></button>
          </span>)}
        {v && off && !play && (
          <span className="relative">
            <button onClick={() => setMenu(menu === "h" ? "" : "h")} title="فيديو تعليمي مخفي" className="grid h-8 w-8 place-items-center rounded-full border border-slate-200 bg-white/80 text-slate-400 opacity-60 shadow-sm hover:opacity-100" data-testid="tut-hidden-btn"><Play className="h-3.5 w-3.5" /></button>
            {menu === "h" && <Menu tid="tut-hidden-menu" onClose={() => setMenu("")} items={[
              [Eye, "إظهار زر الفيديو لهذه الصفحة", () => saveHide(hide.filter((x) => x !== k)), "tut-unhide-this"],
              [Eye, `إظهار كل الفيديوهات المخفية (${hide.length})`, () => saveHide([]), "tut-unhide-all"],
            ]} />}
          </span>)}
        {data.can_edit && (
          <span className="relative">
            <button onClick={() => setMenu(menu === "o" ? "" : "o")} title="الفيديوهات التعليمية" className="inline-flex h-8 items-center gap-1.5 rounded-full border border-rose-300 bg-white px-3 text-xs font-bold text-rose-600 shadow hover:bg-rose-50" data-testid="tut-owner-btn"><Settings2 className="h-4 w-4" /> الفيديوهات التعليمية</button>
            {menu === "o" && <Menu tid="tut-owner-menu" onClose={() => setMenu("")} items={[
              [Pencil, "ضبط فيديو هذه الصفحة", () => setEdit(true), "tut-owner-edit"],
              [Library, "مكتبة الفيديوهات (لوحة مالك المنصة)", () => { sessionStorage.setItem("pc_sec", "tutorials"); window.location.href = "/platform"; }, "tut-owner-library"],
            ]} />}
          </span>)}
      </div>
      {edit && <OwnerEditor k={k} cur={v} onSaved={load} onClose={() => setEdit(false)} />}
      {play && v && <Player v={v} onClose={() => setPlay(false)} />}
      {preview && <Player v={preview} onClose={() => setPreview(null)} />}
    </>
  );
};
